import { Autocomplete, Box, Button, Grid } from '@mui/material';
import { useApi, useDialog, useMessage } from 'app/context';
import { useForm } from 'app/hooks';
import { Breadcrumb, DetailWrapperDiv, InputDatePicker, InputSearchField, SearchCompany, TextField } from 'app/shared';
import { buildErrorDetails, buildErrorMessage, getDefaultJobTitles } from 'app/utils';
import { IPatientCreate } from 'generated/axios';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const PatientCreate = () => {
  const { patientApi } = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const { success, error } = useMessage();
  const navigate = useNavigate();
  const { createDialog, closeDialog } = useDialog();

  //create
  const create = (form: IPatientCreate) => {
    setLoading(true);
    patientApi
      .webCreatePatient(form)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('patient.createSuccess') as string });
        navigate(-1);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
        setErrors(buildErrorDetails(err));
      })
      .finally(() => setLoading(false));
  };

  const { setErrors, bindTextField, submit, form, setValue, bindError } = useForm<IPatientCreate>({
    onSubmit: create,
    requiredFields: ['birth_date', 'first_name', 'last_name'],
    requiredValidationOptions: { errorMsg: t('info.mandatoryField'), validationTime: 'ON_SUBMIT' },
  });

  //search company dialog
  const handleCompanyDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchCompany
          allowCreate
          onCancel={closeDialog}
          onConfirmCompany={(company) => {
            setValue({ field: 'company', value: { name: company.name } });
            closeDialog();
          }}
        />
      ),
    });

  //clear search dialog
  const clearDialog = () => setValue({ field: 'company', value: undefined });

  return (
    <>
      <Breadcrumb />
      <DetailWrapperDiv>
        <Grid container spacing={4}>
          <Grid size={6}>
            <TextField label={t('patient.lastName')} {...bindTextField({ field: 'last_name' })} />
          </Grid>
          <Grid size={6}>
            <TextField label={t('patient.firstName')} {...bindTextField({ field: 'first_name' })} />
          </Grid>
          <Grid size={6}>
            <TextField label={t('patient.taxCode')} {...bindTextField({ field: 'tax_code' })} />
          </Grid>
          <Grid size={6}>
            <InputDatePicker
              label={t('patient.birthDate')}
              defaultValue={null}
              onDatePick={(value) => setValue({ field: 'birth_date', value: value })}
              bindError={() => bindError({ field: 'birth_date' })}
            />
          </Grid>
          <Grid size={6}>
            <InputSearchField
              label={t('patient.company')}
              fieldValue={form.company?.name}
              onSearch={handleCompanyDialog}
              onCancel={clearDialog}
            />
          </Grid>
          <Grid size={6}>
            <Autocomplete
              freeSolo
              options={getDefaultJobTitles()}
              value={form.job_title ?? ''}
              onInputChange={(_e, newValue) =>
                setValue({ field: 'job_title', value: isEmpty(newValue) ? undefined : newValue })
              }
              renderInput={(params) => <TextField {...params} label={t('patient.job')} />}
              disableClearable
            />
          </Grid>
          <Grid size={6}>
            <TextField label={t('patient.address')} {...bindTextField({ field: 'residence' })} />
          </Grid>
        </Grid>
        <Box padding={4}>
          <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={submit}>
            {t('actions.create')}
          </Button>
        </Box>
      </DetailWrapperDiv>
    </>
  );
};
